import { getBundledProductsForSkuId, getIsPaidCoreSeatSku, getProduct, getProductDataIsEmpty, getProductForSkuId, getProductVersion, getQuantityPackDetailsListForProductVersion, getQuantityPackForProductVersion, getSku, getSkuIdIsHub, highestProductLevel, skuIdHasBundledProducts, productHasBundledProducts } from './productsUtils';
import { orderedProductLevels } from 'self-service-api/constants/ProductLevels';
import { createProductConfig } from './productConfigUtils';
import { getNumOfDaysBetween } from './dateUtils';
import Raven from 'raven-js';
import { CORE_PLATFORM_SEATS, ONBOARDING_SERVICES } from 'self-service-api/constants/ProductCategories';
export const getHasSkuInProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;

  if (!productInventory.products) {
    return false;
  }

  return productInventory.products.some(productInventoryObject => {
    if (productInventoryObject.skuId === skuId) return true;
    return productInventoryObject.quantityPackDetails.some(quantityPack => quantityPack.skuId === skuId);
  });
};

const versionNumberComparisonDefault = (ownedVersion, targetVersion) => ownedVersion === targetVersion;

export const getHasProductVersionInProductInventory = (state, {
  skuId,
  apiName,
  versionNumber,
  versionNumberComparison = versionNumberComparisonDefault
}) => {
  const {
    productInventory
  } = state;

  if (!productInventory.products) {
    return false;
  }

  return productInventory.products.some(({
    skuId: ownedSkuId,
    productApiName: ownedApiName
  }) => {
    if (skuId) {
      return ownedSkuId === skuId;
    } else if (versionNumber !== undefined && apiName !== undefined) {
      const {
        versionNumber: ownedVersionNumber
      } = getProductVersion(state, {
        skuId: ownedSkuId
      });
      return apiName === ownedApiName && versionNumberComparison(ownedVersionNumber, versionNumber);
    }

    return false;
  });
};

const productHasAttributeFilters = (state, {
  productAttributeFilters = {},
  productId
}) => {
  return Object.keys(productAttributeFilters).every(attributeFilter => {
    const product = getProduct(state, {
      productId
    });
    if (!product) return false;
    return product[attributeFilter === 'productId' ? 'id' : attributeFilter] === productAttributeFilters[attributeFilter];
  });
};

export const hasProductVersionInBundledProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;

  if (!productInventory.products) {
    return false;
  }

  return productInventory.products.some(({
    bundledProducts
  }) => {
    if (!bundledProducts) return false;
    return bundledProducts.some(({
      productId
    }) => {
      const ownedProduct = getProduct(state, {
        productId
      });
      const productToCheck = getProductForSkuId(state, {
        skuId
      });

      if (!ownedProduct || !productToCheck) {
        return false;
      }

      if (ownedProduct.id === productToCheck.id) {
        return true;
      }

      return false;
    });
  });
};
export const hasPlanBuilderProductsInBundledProductInventory = (state, {
  skuIds
}) => {
  const skuIdsToUse = skuIds.filter(skuId => {
    const productToCheck = getProductForSkuId(state, {
      skuId
    }); // Remove onboarding

    if (productToCheck.productCategory.name === ONBOARDING_SERVICES) {
      return false;
    } // Remove core seats


    if (productToCheck.productCategory.name === CORE_PLATFORM_SEATS) {
      return false;
    }

    return true;
  });

  if (!skuIdsToUse.length) {
    return false;
  }

  return skuIdsToUse.every(skuId => {
    return hasProductVersionInBundledProductInventory(state, {
      skuId
    });
  });
};
export const getProductVersionsFromProductInventory = (state, {
  productAttributeFilters = {}
} = {}) => {
  const {
    productInventory
  } = state;
  const isEmpty = getProductDataIsEmpty(state);

  if (isEmpty) {
    // Generally this means that we call this util before all the data is fetched.
    // If we see this error investigate what race condition might be causing it and update accordingly
    console.error('Pricing pages lib: Attempted fetch of empty product inventory data');
    Raven.captureException(new Error('Pricing pages lib: Attempted fetch of empty product inventory data'));
    return [];
  }

  return productInventory.products ? productInventory.products.map(({
    skuId
  }) => getProductVersion(state, {
    skuId
  })).filter(productVersion => productVersion).filter(({
    productId,
    skuId
  }) => productHasAttributeFilters(state, {
    productAttributeFilters,
    productId
  }) || skuIdHasBundledProducts(state, {
    skuId
  }) && getBundledProductsForSkuId(state, {
    skuId
  }).find(product => product ? productHasAttributeFilters(state, {
    productAttributeFilters,
    productId: product.id
  }) : false)) : [];
};
export const getProductVersionFromProductInventory = (state, {
  productAttributeFilters
}) => {
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters
  });
  if (ownedProductVersions.length === 0) return null;

  if (ownedProductVersions.length > 1) {
    const message = 'Unexpected multiple products found in ownedProductVersionSelector';
    console.error(message, {
      ownedProductVersions,
      productAttributeFilters
    });
  }

  return ownedProductVersions[0];
};
export const getProductsFromProductInventory = (state, {
  productAttributeFilters = {}
} = {}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters
  });
  const ownedProductIds = [];
  ownedProductVersions.forEach(({
    skuId
  }) => {
    const productVersion = skuMap[skuId];

    if (productVersion && !ownedProductIds.includes(productVersion.productId)) {
      ownedProductIds.push(productVersion.productId);
    }
  });
  return ownedProductIds.map(productId => getProduct(state, {
    productId
  })).filter(product => product !== null);
};
export const getHasProductInProductInventory = (state, {
  productAttributeFilters
}) => {
  const ownedProducts = getProductsFromProductInventory(state, {
    productAttributeFilters
  });
  if (!ownedProducts) return false;
  return ownedProducts.length > 0;
};
export const getProductsFromProductInventoryQuantity = (state, {
  skuId
}) => {
  const {
    productInventory,
    productData: {
      skuMap
    }
  } = state;
  const productsFromProductInventory = productInventory.products;

  if (!productsFromProductInventory) {
    return 0;
  }

  for (let i = 0; i < productsFromProductInventory.length; i++) {
    if (productsFromProductInventory[i].skuId === skuId) {
      return productsFromProductInventory[i].skuQuantityPurchased;
    }

    const quantityPacks = productsFromProductInventory[i].quantityPackDetails;

    if (quantityPacks) {
      for (let p = 0; p < quantityPacks.length; p++) {
        if (quantityPacks[p].skuId === skuId) {
          const {
            quantityUnit: {
              includedWithQuantityPack
            }
          } = skuMap[quantityPacks[p].skuId];
          return quantityPacks[p].skuQuantityPurchased * includedWithQuantityPack;
        }
      }
    }
  }

  return 0;
};
export const getProductsFromProductInventoryQuantityPackQuantity = (state, {
  productIdentifier,
  unitType,
  unitScope
}) => {
  const {
    productInventory
  } = state;
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters: Object.assign({}, productIdentifier)
  });
  if (!productVersion) return 0;
  const quantityPack = getQuantityPackForProductVersion(state, {
    productVersionSkuId: productVersion.skuId,
    unitType,
    unitScope
  });
  if (!quantityPack) return 0;
  const {
    skuId: quantityPackSkuId,
    quantityUnit: {
      includedWithQuantityPack
    }
  } = quantityPack;

  if (!productInventory.products) {
    return 0;
  } // Search through all owned quantity packs for the right skuId


  for (const ownedProductVersion of productInventory.products) {
    for (const ownedQuantityPack of ownedProductVersion.quantityPackDetails || []) {
      if (ownedQuantityPack.skuId === quantityPackSkuId) {
        return ownedQuantityPack.skuQuantityPurchased * includedWithQuantityPack;
      }
    }
  }

  return 0;
};
export const getHighestProductLevelFromProductInventory = (state, {
  productLine
} = {}) => {
  const ownedProducts = productLine ? getProductsFromProductInventory(state, {
    productAttributeFilters: {
      productLine
    }
  }) : getProductsFromProductInventory(state);
  return highestProductLevel(...ownedProducts.filter(product => product.productLine && product.productLevel || productHasBundledProducts(state, {
    productId: product.id
  })).map(product => product.productLevel));
};
export const getAdditionalUnitsInProductLineFromProductInventory = (state, {
  productLine,
  unitType,
  unitScope
}) => {
  const productIdentifier = {
    productLine,
    productLevel: getHighestProductLevelFromProductInventory(state, {
      productLine
    })
  };
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters: productIdentifier
  });
  if (!productVersion) return null;
  const {
    skuId
  } = productVersion;
  const quantityPack = getQuantityPackForProductVersion(state, {
    productVersionSkuId: skuId,
    unitType,
    unitScope
  });
  if (!quantityPack) return null;
  const {
    quantityUnit: {
      includedWithProductVersion
    }
  } = quantityPack;
  return includedWithProductVersion + getProductsFromProductInventoryQuantityPackQuantity(state, {
    productIdentifier,
    unitType,
    unitScope
  });
};
export const getHasPaidHubInProductInventory = (state, {
  productLevels = []
}) => {
  const productVersions = getProductVersionsFromProductInventory(state);
  return productVersions.some(({
    skuId
  }) => {
    const isPaidHubOrBundle = getSkuIdIsHub(state, {
      skuId
    }) || skuIdHasBundledProducts(state, {
      skuId
    });

    if (productLevels.length) {
      const {
        productLevel: ownedProductLevel
      } = getProductForSkuId(state, {
        skuId
      });
      return isPaidHubOrBundle && productLevels.includes(ownedProductLevel);
    }

    return isPaidHubOrBundle;
  });
};
export const getHasProductVersionOrHigherProductLevelInProductInventory = (state, {
  skuId
}) => {
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters: {
      productLine
    }
  });
  if (ownedProductVersions.length === 0) return false;else {
    return ownedProductVersions.some(({
      skuId: ownedSkuId
    }) => {
      const {
        productLevel: ownedProductLevel
      } = getProductForSkuId(state, {
        skuId: ownedSkuId
      }); // Have to check the skuId specifically so legacy products don't count as being owned

      return orderedProductLevels.indexOf(ownedProductLevel) > orderedProductLevels.indexOf(productLevel) || ownedSkuId === skuId;
    });
  }
};
export const getQuantityPackProductConfigurationsFromProductInventory = (state, {
  baseProductConfigurations
}) => {
  const ownedQuantityPackProductConfigurations = [];
  baseProductConfigurations.forEach(({
    skuId
  }) => {
    const quantityPacks = getQuantityPackDetailsListForProductVersion(state, {
      productVersionSkuId: skuId
    });
    quantityPacks.forEach(quantityPack => {
      const {
        skuId: quantityPackSkuId,
        productId,
        quantityUnit: {
          unitType,
          unitScope
        }
      } = quantityPack;
      const ownedQuantity = getProductsFromProductInventoryQuantityPackQuantity(state, {
        productIdentifier: {
          productId
        },
        unitType,
        unitScope
      });
      if (ownedQuantity) ownedQuantityPackProductConfigurations.push(createProductConfig({
        skuId: quantityPackSkuId,
        quantity: ownedQuantity
      }));
    });
  });
  return ownedQuantityPackProductConfigurations;
};
export const getProductStartDateFromProductInventory = (state, {
  productAttributeFilters
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) return {};
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters
  });
  if (!productVersion) return {};
  const {
    skuId
  } = productVersion;
  const foundProductInventoryObject = productInventory.products.find(productInventoryObject => {
    if (productInventoryObject.skuId === skuId) return true;
    return productInventoryObject.quantityPackDetails.some(quantityPack => quantityPack.skuId === skuId);
  });
  const [year, month, day] = foundProductInventoryObject ? foundProductInventoryObject.startDate : [];
  return {
    year,
    month,
    day
  };
};
export const getDaysSinceProductStartDateFromProductInventory = (state, {
  productAttributeFilters
}) => {
  const {
    year,
    month,
    day
  } = getProductStartDateFromProductInventory(state, {
    productAttributeFilters
  });
  if (!year && !month && !day) return 0;
  const today = new Date();
  const startDate = new Date(year, month, day);
  return getNumOfDaysBetween(startDate, today);
};
export const getIsSkuUpgraded = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = getSku(state, {
    skuId
  });
  const ownedDowngradedSku = productInventory.products.find(productInventoryProductObject => {
    const {
      productLine: ownedProductLine,
      productLevel: ownedProductLevel
    } = getProductForSkuId(state, {
      skuId: productInventoryProductObject.skuId
    });
    const {
      skuType: ownedSkuType
    } = getSku(state, {
      skuId: productInventoryProductObject.skuId
    });
    const isHigherProductLevel = orderedProductLevels.indexOf(productLevel) > orderedProductLevels.indexOf(ownedProductLevel);
    const isSameProductLine = productLine ? productLine === ownedProductLine : true;
    const isSameSkuType = skuType === ownedSkuType;
    return isSameSkuType && isHigherProductLevel && isSameProductLine;
  });
  return Boolean(ownedDowngradedSku);
};
export const getCurrentTermTotalDiscountsForOwnedSkuId = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  const ownedProducts = productInventory.products;
  if (!ownedProducts || !ownedProducts.length) return null;
  const ownedProduct = ownedProducts.find(product => product.skuId === skuId);
  if (!ownedProduct) return null;
  const currentTermDiscount = ownedProduct.currentTerm.discount;
  const discretionaryDiscountPercent = currentTermDiscount.discretionaryPercentDiscount || 0;
  const namedDiscountPercents = currentTermDiscount.namedPercentDiscounts.reduce((acc, namedDiscount) => acc + namedDiscount.discount, 0);
  const percentDiscounts = discretionaryDiscountPercent + namedDiscountPercents;
  const fixedDiscounts = currentTermDiscount.namedFixedDiscounts.reduce((acc, fixedDiscount) => acc + fixedDiscount.discount, 0);
  return {
    percentDiscounts,
    fixedDiscounts
  };
};
export const getCrmSuiteOwnershipLevel = state => {
  const {
    productInventory
  } = state;
  return productInventory.crmSuiteLevel;
};
export const getCurrentOwnedPaidCoreSeatsTotal = state => {
  const {
    productInventory
  } = state;
  const products = productInventory.products;
  const paidSeatSku = products.find(product => {
    const skuId = product.skuId;
    return getIsPaidCoreSeatSku(state, {
      skuId
    });
  });

  if (paidSeatSku) {
    return getProductsFromProductInventoryQuantity(state, {
      skuId: paidSeatSku.skuId
    });
  }

  return 0;
};